import { useMemo } from 'react'
import { Clock, Table2, Users } from 'lucide-react'

import { cn } from 'lib/utils'
import type { ExplorerReport } from '../types/explorer.types'

/**
 * Recent Reports sidebar for the Explorer screen.
 *
 * The other index screens put a "Recent Activity" panel here listing the
 * records they were last near. Explorer has no single record type (the grain
 * tabs change it), so the equivalent thing to be near is the saved reports for
 * whichever grain is open, most recently updated first.
 *
 * It renders from the reports the screen already holds, so it costs no fetch
 * of its own and stays in step with a save, rename or delete without asking.
 */

const RECENT_LIMIT = 10

function formatActivityDate(dateStr?: string): string {
	if (!dateStr) return ''
	const d = new Date(dateStr)
	if (Number.isNaN(d.getTime())) return ''
	const now = new Date()
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const sevenDaysAgo = new Date(todayStart)
	sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

	if (d >= todayStart) {
		return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
	}
	if (d >= sevenDaysAgo) {
		return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
	}
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export interface SidebarExplorerReportsProps {
	/** Saved reports for the grain currently open. */
	reports: ExplorerReport[]
	/** The report open in the grid, highlighted in the list. */
	loadedReportId: string | null
	isLoading: boolean
	onOpen: (report: ExplorerReport) => void
}

export function SidebarExplorerReports({
	reports,
	loadedReportId,
	isLoading,
	onOpen,
}: SidebarExplorerReportsProps) {
	const recents = useMemo(() => {
		const byRecency = [...reports].sort((a, b) => {
			const ta = new Date(a?.updatedAt || a?.createdAt || 0).getTime()
			const tb = new Date(b?.updatedAt || b?.createdAt || 0).getTime()
			return tb - ta
		})
		return byRecency.slice(0, RECENT_LIMIT)
	}, [reports])

	// A spinner over a list that is already on screen reads as the list going
	// away, so only an empty panel says it is still loading.
	const showLoading = isLoading && recents.length === 0

	return (
		<div className="flex flex-col h-full">
			<div className="px-4 py-3 border-b border-gray-100">
				<div className="flex items-center gap-2">
					<Clock className="w-4 h-4 text-gray-400" />
					<span className="text-sm font-semibold text-gray-900">Recent Reports</span>
					{recents.length > 0 && (
						<span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
							{recents.length}
						</span>
					)}
				</div>
			</div>

			<div className="flex-1 overflow-y-auto">
				{showLoading ? (
					<div className="flex items-center justify-center h-full p-4">
						<span className="text-sm text-gray-400">Loading...</span>
					</div>
				) : recents.length === 0 ? (
					<div className="text-center py-6 px-4">
						<Table2 className="w-5 h-5 mx-auto text-gray-300 mb-2" />
						<p className="text-sm text-gray-400">No saved reports yet</p>
						<p className="mt-1 text-xs text-gray-400">
							Build a layout and save it to find it here.
						</p>
					</div>
				) : (
					<div className="flow-root px-4 pt-3">
						<ul className="-mb-6">
							{recents.map((report, idx) => {
								const isLast = idx === recents.length - 1
								const isOpen = report.id === loadedReportId
								return (
									<li key={report.id} className="relative pb-6 last:pb-0">
										{!isLast && (
											<span
												className="absolute left-[15px] top-8 -ml-px h-full w-0.5 bg-gray-200"
												aria-hidden="true"
											/>
										)}
										<button
											type="button"
											onClick={() => onOpen(report)}
											aria-current={isOpen ? 'true' : undefined}
											className="relative flex w-full items-start gap-3 text-left group"
										>
											<span
												className={cn(
													'h-8 w-8 shrink-0 rounded-full flex items-center justify-center',
													isOpen ? 'bg-[#0080fe] text-white' : 'bg-blue-50 text-blue-600',
												)}
											>
												<Table2 className="w-3.5 h-3.5" />
											</span>
											<span className="min-w-0 flex-1 pt-0.5">
												<span className="flex items-center gap-1.5">
													<span
														className={cn(
															'text-sm font-medium truncate transition-colors',
															isOpen
																? 'text-[#0080fe]'
																: 'text-gray-900 group-hover:text-[#0080fe]',
														)}
														title={report.name}
													>
														{report.name}
													</span>
													{/* Someone else's shared layout, marked the same way the
													    saved-report picker marks it. */}
													{!report.isOwner && report.visibility === 'shared' && (
														<Users className="w-3.5 h-3.5 shrink-0 text-gray-400" />
													)}
												</span>
												{report.description && (
													<span className="mt-0.5 block text-xs text-gray-500 truncate" title={report.description}>
														{report.description}
													</span>
												)}
												<span className="mt-0.5 block text-xs text-gray-400">
													{formatActivityDate(report.updatedAt || report.createdAt)}
												</span>
											</span>
										</button>
									</li>
								)
							})}
						</ul>
					</div>
				)}
			</div>
		</div>
	)
}

export default SidebarExplorerReports
